"use client"

import DashboardContentWrapper from "../../../../shared/components/dashboard-content-wrapper"
import { Button } from "../../../../shared/components/ui/button"
import Heading from "../../../../shared/components/ui/typography/heading"

import { DASHBOARD_ROUTE } from "../../../../shared/constants/pages-routes"
import { buildRegionAwareHref } from "../../../../shared/lib/utils"
import WorkOrdersTablePagination from "@/features/work-orders/components/work-orders-table-pagination"
import { Plus } from "lucide-react"
import Link from "next/link"
import React, { useMemo } from "react"
import { useSearchParams } from "next/navigation"
import WorkOrdersCardsMobile from "@/features/work-orders/components/work-orders-cards-mobile"
import PaginationDataContextProvider from "@/features/work-orders/context/pagination-data-context"
import WorkOrdersList from "@/features/work-orders/components/work-orders-list"
import WorkOrdersSearch from "@/features/work-orders/components/work-orders-search"
import WorkOrdersFilterButton from "@/features/work-orders/components/work-orders-filter-button"
import { useSession } from "@/features/auth/context/session.context"
import { UserRole } from "@/shared/constants/user-roles"
import { useRegion } from "../../../../shared/hooks/use-region"
import LocationSelector from "../../../../shared/components/location-selector"
import ScrollToTopFab from "@/shared/components/scroll-to-top-fab"
import WorkOrderTabs from "@/features/work-orders/components/WorkOrderTabs"
import { JobBoardContainer } from "@/features/work-orders/components/job-board/JobBoardContainer"
import { TodayWorkOrdersView } from "@/features/work-orders/components/job-board/TodayWorkOrdersView"
import {
  SEARCH_PARAMS_TAB_KEY,
  WORK_ORDER_TAB_TODAY,
  WORK_ORDER_TAB_JOB_BOARD,
  WORK_ORDER_TAB_LIST,
  WorkOrderTabValue,
} from "@/features/work-orders/constants/work-order-tab-links"
import { useFeatureFlag } from "@/features/feature-flags/hooks/use-feature-flag"

function WorkOrdersPage() {
    const { session } = useSession()
    const { regionSlug } = useRegion()
    const searchParams = useSearchParams()
    const isJobBoardEnabled = useFeatureFlag('job_board')

    const tabParam = searchParams.get(SEARCH_PARAMS_TAB_KEY)
    const effectiveTab: WorkOrderTabValue = useMemo(() => {
        if (tabParam === WORK_ORDER_TAB_TODAY) return WORK_ORDER_TAB_TODAY
        if (tabParam === WORK_ORDER_TAB_LIST) return WORK_ORDER_TAB_LIST
        if (tabParam === WORK_ORDER_TAB_JOB_BOARD && isJobBoardEnabled) return WORK_ORDER_TAB_JOB_BOARD

        // Default for Volunteers and Team Leaders is Today view
        if (
            session?.role === UserRole.VOLUNTEER ||
            session?.role === UserRole.TEAM_LEADER
        ) {
            return WORK_ORDER_TAB_TODAY
        }
        // For Planners, default to Job Board if enabled, otherwise List view
        return isJobBoardEnabled ? WORK_ORDER_TAB_JOB_BOARD : WORK_ORDER_TAB_LIST
    }, [tabParam, session?.role, isJobBoardEnabled])

    const createHref = React.useMemo(() =>
        buildRegionAwareHref(regionSlug, DASHBOARD_ROUTE.createWorkOrders),
    [regionSlug])

    // Check if user is Admin or BaseLeader
    const canCreateWorkOrder =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER

    // Privacy: hide filters for Volunteer, Homeowner, Supporter (search only)
    const hideFilters = [
        UserRole.VOLUNTEER,
        UserRole.HOMEOWNER,
        UserRole.Supporter,
    ].includes(session?.role as UserRole)

    return (
        <DashboardContentWrapper className="space-y-5 pb-24 md:pb-5">
            <div className="flex flex-col gap-4 md:flex-row md:justify-between items-start md:items-center">
                <Heading>Work Orders</Heading>
                <div className="flex flex-wrap items-center gap-4">
                    <WorkOrderTabs tab={effectiveTab} />
                    <LocationSelector />
                </div>
            </div>

            {effectiveTab === WORK_ORDER_TAB_TODAY ? (
                <TodayWorkOrdersView />
            ) : effectiveTab === WORK_ORDER_TAB_JOB_BOARD && isJobBoardEnabled ? (
                <JobBoardContainer />
            ) : (
                <PaginationDataContextProvider>
                    {/* table wrapper */}
                    <div className="border rounded-xl px-3 py-5 space-y-4 mb-10 hidden xl:block bg-card shadow-sm">
                        <div className="flex flex-col gap-4">
                            {/* Desktop: filter/search (center), Create Work Order (right) */}
                            <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 items-center">
                                {/* Center: Filter and Search (Filter hidden for Volunteer, Homeowner, Supporter) */}
                                <div className="flex gap-4 items-center justify-center min-w-0">
                                    {!hideFilters && <WorkOrdersFilterButton />}
                                    <div className="flex-1 w-full">
                                        <WorkOrdersSearch />
                                    </div>
                                </div>
                                
                                {/* Right: Create Work Order - Visible to Admin and Base Leader users */}
                                {canCreateWorkOrder && (
                                    <div className="flex items-center justify-end">
                                        <Button asChild className="bg-primary hover:bg-primary/90 text-white font-bold rounded-md gap-2 py-1 h-10">
                                            <Link href={createHref}>
                                                <Plus className="w-5 h-5" />
                                                Create Work Order
                                            </Link>
                                        </Button>
                                    </div>
                                )}
                            </div>
                        </div>
                        <WorkOrdersList />
                        <WorkOrdersTablePagination />
                    </div>
                    <div className="block xl:hidden">
                        <WorkOrdersCardsMobile />
                        <div className="mt-6">
                            <WorkOrdersTablePagination />
                        </div>
                    </div>
                </PaginationDataContextProvider>
            )}

            <ScrollToTopFab />
        </DashboardContentWrapper>
    )
}

export default WorkOrdersPage

